/**
 * Narzędzia do bezpiecznego zarządzania hasłami dla kluczy szyfrowania
 */

import { getDB } from './indexedDb';

// Interfejs dla przechowywanego hasła
interface StoredPassword {
  id: string;
  userId: string;
  sessionOnly: boolean;
  expiresAt: string | null;
  createdAt: string;
}

// Klucz do przechowywania hasła w sesji
const SESSION_PASSWORD_KEY = 'temp-encryption-password';

/**
 * Zapisuje hasło w pamięci sesji (tymczasowo)
 * @param userId ID użytkownika
 * @param password Hasło do zapisania
 * @param sessionOnly Czy hasło ma być przechowywane tylko w sesji (true) czy też w IndexedDB (false)
 * @param expirationMinutes Czas wygaśnięcia hasła w minutach (null = bez wygaśnięcia)
 */
export const storePassword = async (
  userId: string,
  password: string,
  sessionOnly: boolean = true,
  expirationMinutes: number | null = 24 * 60
): Promise<void> => {
  // Zapisz hasło w sesji
  sessionStorage.setItem(SESSION_PASSWORD_KEY, password);
  
  // Oblicz czas wygaśnięcia
  const expiresAt = expirationMinutes 
    ? new Date(Date.now() + expirationMinutes * 60 * 1000).toISOString()
    : null;
  
  // Jeśli nie jest tylko dla sesji, zapisz też w IndexedDB
  if (!sessionOnly) {
    const passwordRecord: StoredPassword = {
      id: `password-${userId}`,
      userId,
      sessionOnly,
      expiresAt,
      createdAt: new Date().toISOString()
    };
    
    const db = await getDB();
    await db.put('encryption_keys', passwordRecord);
  }
  
  console.log(`🔐 Hasło zapisane ${sessionOnly ? 'w sesji' : 'w bazie lokalnej'}`);
};

/**
 * Pobiera hasło z pamięci sesji lub IndexedDB
 * @param userId ID użytkownika
 * @returns Hasło lub null, jeśli nie znaleziono lub wygasło
 */
export const getPassword = async (userId: string): Promise<string | null> => {
  // Najpierw sprawdź w sesji
  const sessionPassword = sessionStorage.getItem(SESSION_PASSWORD_KEY);
  if (sessionPassword) {
    return sessionPassword;
  }
  
  // Jeśli nie ma w sesji, sprawdź w IndexedDB
  try {
    const db = await getDB();
    const passwordRecord = await db.get('encryption_keys', `password-${userId}`) as StoredPassword | undefined;
    
    if (!passwordRecord) {
      return null;
    }
    
    // Sprawdź, czy hasło nie wygasło
    if (passwordRecord.expiresAt) {
      const expiryTime = new Date(passwordRecord.expiresAt).getTime();
      if (expiryTime < Date.now()) {
        console.log('⌛ Hasło wygasło, usuwam z bazy');
        await db.delete('encryption_keys', `password-${userId}`);
        return null;
      }
    }
    
    // Jeśli hasło jest tylko dla sesji, nie powinno być w IndexedDB
    // To jest dodatkowe zabezpieczenie
    if (passwordRecord.sessionOnly) {
      console.warn('⚠️ Znaleziono hasło sesyjne w bazie lokalnej, usuwam');
      await db.delete('encryption_keys', `password-${userId}`);
      return null;
    }
    
    return 'stored-password'; // W rzeczywistości hasło nie jest przechowywane, tylko token
  } catch (error) {
    console.error('❌ Błąd pobierania hasła:', error);
    return null;
  }
};

/**
 * Usuwa hasło z pamięci sesji i IndexedDB
 * @param userId ID użytkownika
 */
export const clearPassword = async (userId: string): Promise<void> => {
  // Usuń z sesji
  sessionStorage.removeItem(SESSION_PASSWORD_KEY);
  
  // Usuń z IndexedDB
  try {
    const db = await getDB();
    await db.delete('encryption_keys', `password-${userId}`);
    console.log('🔒 Hasło usunięte');
  } catch (error) {
    console.error('❌ Błąd usuwania hasła:', error);
  }
};

/**
 * Sprawdza, czy hasło jest dostępne w sesji lub IndexedDB
 * @param userId ID użytkownika
 * @returns true, jeśli hasło jest dostępne
 */
export const isPasswordAvailable = async (userId: string): Promise<boolean> => {
  const password = await getPassword(userId);
  return password !== null;
};

/**
 * Prosi użytkownika o podanie hasła
 * @param message Komunikat dla użytkownika
 * @returns Hasło lub null, jeśli użytkownik anulował
 */
export const promptForPassword = async (message: string = 'Podaj hasło do odszyfrowania danych'): Promise<string | null> => {
  // W rzeczywistej aplikacji użylibyśmy komponentu React do wyświetlenia modalu
  // Tutaj używamy prostego prompt, który nie jest bezpieczny dla produkcji
  const password = prompt(message);
  return password;
};

/**
 * Pobiera hasło z sesji, IndexedDB lub prosi użytkownika
 * @param userId ID użytkownika
 * @param message Komunikat dla użytkownika
 * @returns Hasło lub null, jeśli nie udało się pobrać
 */
export const getOrPromptForPassword = async (
  userId: string,
  message: string = 'Podaj hasło do odszyfrowania danych'
): Promise<string | null> => {
  // Najpierw spróbuj pobrać z sesji lub IndexedDB
  const storedPassword = await getPassword(userId);
  if (storedPassword) {
    return storedPassword;
  }
  
  // Jeśli nie ma, poproś użytkownika
  const password = await promptForPassword(message);
  if (password) {
    // Zapisz hasło w sesji na 24 godziny
    await storePassword(userId, password, true, 24 * 60);
  }
  
  return password;
};